import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/use-toast';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { useUserSettings } from '@/OS/hooks/useUserSettings';
import {
  Shield,
  Braces,
  Database,
  Bot,
  Rocket,
  FileSpreadsheet,
  Octagon,
  OctagonAlert,
  Settings,
  Activity,
  Lock,
  Key,
  Network,
} from 'lucide-react';
import { setOSAIBlocked, isOSAIBlocked } from '@/lib/osIntegration';
import { useSupabaseStore } from '@/store/SupabaseStore';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import OSModelSelector from './OSModelSelector';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  clearAllowlistPrefixes,
  disableAgentGuard,
  enableAgentGuard,
  getAllowlistPrefixes,
  isAgentGuardEnabled,
} from '@/lib/agentAccess';
import { useAppStore } from '@/store';
import { useProfile } from '@/hooks/useProfile';
import OpenAISettings from '@/components/settings/OpenAISettings';
import OllamaSettings from '@/components/settings/OllamaSettings';
import GeminiSettings from '@/components/settings/GeminiSettings';
import DeepSeekSettings from '@/components/settings/DeepSeekSettings';
import CohereSettings from '@/components/settings/CohereSettings';
import MaplePTSettings from '@/components/settings/MaplePTSettings';
import SealedMemory from '@/components/settings/SealedMemory';

const TRUST_DEFAULT_KEY = 'trust_mode_enabled';
const TRUST_CITATIONS_KEY = 'trust_require_citations';
const TRUST_ATTESTATION_KEY = 'trust_require_attestation';
const TRUST_FLOOR_KEY = 'trust_floor';

type AICenterProps = {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
};

type TileProps = {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  actionText: string;
  onAction: () => void;
  variant?: 'default' | 'danger';
};

const getStoredBoolean = (key: string, fallback: boolean) => {
  try {
    const value = localStorage.getItem(key);
    if (value === null) return fallback;
    return value === 'true';
  } catch {
    return fallback;
  }
};

const getStoredNumber = (key: string, fallback: number) => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const Tile = ({ icon: Icon, title, subtitle, actionText, onAction, variant = 'default' }: TileProps) => (
  <Card className={`transition hover:ring-2 hover:ring-primary ${variant === 'danger' ? 'border-destructive' : ''}`}>
    <CardContent className="flex items-start gap-3 p-4">
      <div className={`rounded-md p-2 ${variant === 'danger' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium">{title}</div>
        {subtitle ? <div className="mt-0.5 text-xs text-muted-foreground">{subtitle}</div> : null}
      </div>
      <Button size="sm" variant={variant === 'danger' ? 'destructive' : 'secondary'} onClick={onAction}>
        {actionText}
      </Button>
    </CardContent>
  </Card>
);

export const AICenter: React.FC<AICenterProps> = ({ onOpenWindow }) => {
  const { toast } = useToast();
  const { profile } = useProfile();
  const logOperatorAction = useSupabaseStore((state) => state.logOperatorAction);
  const { deleteAllChats, refreshChats } = useSupabaseStore();
  const { saveHistory, setSaveHistory, allowAnalytics, setAllowAnalytics } = useAppStore();

  const [activeTab, setActiveTab] = useState('overview');
  const [clientName, setClientName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [confirmDeleteHistoryOpen, setConfirmDeleteHistoryOpen] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [trustEnabled, setTrustEnabled] = useState(true);
  const [requireCitations, setRequireCitations] = useState(false);
  const [requireAttestation, setRequireAttestation] = useState(false);
  const [trustFloor, setTrustFloor] = useState(0.83);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { settings: globalAnalytics, updateSetting: updateGlobalAnalytics } = useUserSettings('emulation-analytics', { enabled: false });
  const { settings: osAISettings, updateSetting: updateOSAISetting } = useUserSettings('mapleos-ai', {
    defaultModelId: 'maplept-free',
    hideDesktopTaskbar: false,
  });

  useEffect(() => {
    setBlocked(isOSAIBlocked());
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ blocked: boolean }>).detail;
      if (detail) setBlocked(detail.blocked);
    };

    window.addEventListener('os:ai-block-changed', handler as EventListener);
    return () => window.removeEventListener('os:ai-block-changed', handler as EventListener);
  }, []);

  useEffect(() => {
    setTrustEnabled(getStoredBoolean(TRUST_DEFAULT_KEY, true));
    setRequireCitations(getStoredBoolean(TRUST_CITATIONS_KEY, false));
    setRequireAttestation(getStoredBoolean(TRUST_ATTESTATION_KEY, false));
    setTrustFloor(getStoredNumber(TRUST_FLOOR_KEY, 0.83));
  }, []);


  const openProtectionHistory = () => {
    if (!onOpenWindow) return;
    onOpenWindow('protection-history', 'Protection History');
  };

  const openTaskManager = () => {
    if (!onOpenWindow) return;
    onOpenWindow('ai-task-manager', 'AI Task Manager');
  };

  const openIsolation = () => {
    if (!onOpenWindow) return;
    onOpenWindow('ai-isolation', 'AI Isolation');
  };

  const openEncryptionSettings = () => {
    if (!onOpenWindow) return;
    onOpenWindow('encryption-settings', 'Encryption Settings');
  };

  const confirmStop = () => setShowStopDialog(true);

  const performStop = () => {
    setOSAIBlocked(true);
    setBlocked(true);
    setShowStopDialog(false);
    void logOperatorAction('ai_access_stop', { source: 'AICenter' });
    toast({
      title: 'AI access stopped',
      description: 'AI is now jailed inside MapleOS until you resume access.',
      variant: 'destructive',
    });
  };

  const resumeAccess = () => {
    setOSAIBlocked(false);
    setBlocked(false);
    void logOperatorAction('ai_access_resume', { source: 'AICenter' });
    toast({
      title: 'AI access resumed',
      description: 'AI tools can access OS files again where enabled.',
    });
  };

  const persistTrustBoolean = (key: string, value: boolean) => {
    try {
      localStorage.setItem(key, value ? 'true' : 'false');
    } catch {
      return;
    }
  };

  const handleTrustModeChange = (checked: boolean) => {
    setTrustEnabled(checked);
    persistTrustBoolean(TRUST_DEFAULT_KEY, checked);
    void logOperatorAction(checked ? 'trust_mode_enable' : 'trust_mode_disable', { source: 'AICenter' });
    toast({
      title: checked ? 'Trust Mode enabled' : 'Trust Mode disabled',
      description: checked
        ? 'EHCO Trust rules will be enforced across MapleOS AI workflows.'
        : 'EHCO Trust rules are no longer enforced by default.',
    });
  };

  const handleRequireCitationsChange = (checked: boolean) => {
    setRequireCitations(checked);
    persistTrustBoolean(TRUST_CITATIONS_KEY, checked);
  };

  const handleRequireAttestationChange = (checked: boolean) => {
    setRequireAttestation(checked);
    persistTrustBoolean(TRUST_ATTESTATION_KEY, checked);
  };

  const handleTrustFloorChange = (values: number[]) => {
    const value = Number(values[0] ?? 0.83);
    setTrustFloor(value);
    try {
      localStorage.setItem(TRUST_FLOOR_KEY, String(value));
    } catch {
      return;
    }
  };

  const handleSaveHistoryChange = (checked: boolean) => {
    if (!checked && profile?.id && saveHistory) {
      setConfirmDeleteHistoryOpen(true);
      return;
    }
    setSaveHistory(checked);
  };

  const handleHideTaskbarChange = (checked: boolean) => {
    updateOSAISetting('hideDesktopTaskbar', checked);
    window.dispatchEvent(
      new CustomEvent('mapleos-ai-settings-changed', {
        detail: { hideDesktopTaskbar: checked },
      })
    );
    toast({
      title: checked ? 'AI Taskbar hidden' : 'AI Taskbar shown',
      description: checked
        ? 'The desktop AI Taskbar is now hidden for your account.'
        : 'The desktop AI Taskbar is visible again for your account.',
    });
  };

  const handleConfirmDeleteAndDisableHistory = async () => {
    if (!profile?.id) {
      setSaveHistory(false);
      return;
    }

    try {
      await deleteAllChats(profile.id);
      setSaveHistory(false);
      await refreshChats();
      toast({
        title: 'Chat history removed',
        description: 'Saved chat history has been deleted and history is now off.',
      });
    } catch (error) {
      console.error('[AICenter] Failed to delete chats and disable history', error);
      toast({
        title: 'Could not disable history',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const runClientWorkspace = () => {
    const name = clientName.trim() || 'Client';
    window.dispatchEvent(
      new CustomEvent('mapleos-apply-template', {
        detail: { name: 'client-workspace', vars: { ClientName: name } },
      })
    );
    toast({ title: 'Client workspace created', description: `Workspace for ${name} is ready.` });
  };

  const runDataBriefing = () => {
    const text = csvText.trim();
    if (!text) {
      toast({ title: 'No CSV provided', description: 'Paste CSV text first.', variant: 'destructive' });
      return;
    }

    window.dispatchEvent(
      new CustomEvent('mapleos-csv-to-findings', {
        detail: { csvText: text },
      })
    );
    toast({ title: 'Briefing generated', description: 'Findings and deck created.' });
  };

  const handleCsvFile = async (file: File | null) => {
    if (!file) return;
    const content = await file.text();
    setCsvText(content);
    setSelectedFileName(file.name);
    toast({ title: 'CSV loaded', description: `Loaded ${file.name}` });
  };

  const allowlist = getAllowlistPrefixes();
  const guardOn = isAgentGuardEnabled();

  return (
    <div className="flex h-full w-full">
      <aside className="flex w-12 flex-col items-center gap-2 border-r bg-background py-3 sm:w-14">
        <Button
          variant="ghost"
          size="icon"
          title="Protection History"
          aria-label="Protection History"
          onClick={openProtectionHistory}
          className="h-10 w-10"
        >
          <Shield className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title="AI Task Manager"
          aria-label="AI Task Manager"
          onClick={openTaskManager}
          className="h-10 w-10"
        >
          <Activity className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title="AI Isolation"
          aria-label="AI Isolation"
          onClick={openIsolation}
          className="h-10 w-10"
        >
          <Lock className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          title="Encryption Settings"
          aria-label="Encryption Settings"
          onClick={openEncryptionSettings}
          className="h-10 w-10"
        >
          <Key className="h-5 w-5" />
        </Button>
      </aside>

      <div className="flex min-h-0 flex-1 flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full flex-col">
          <div className="border-b bg-background/95 px-4 py-4 backdrop-blur">
            <div className="mb-4">
              <h1 className="text-xl font-semibold">AI Center</h1>
              <p className="text-sm text-muted-foreground">
                MapleOS controls for trusted AI operations, privacy, integrations, and sealed memory.
              </p>
            </div>
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="trust">Trust &amp; Governance</TabsTrigger>
              <TabsTrigger value="privacy">Privacy</TabsTrigger>
              <TabsTrigger value="integrations">API Integrations</TabsTrigger>
              <TabsTrigger value="sealed">Sealed Memory</TabsTrigger>
            </TabsList>
          </div>

          <div className="min-h-0 flex-1">
            <ScrollArea className="h-full">
              <div className="p-4">
                <TabsContent value="overview" className="mt-0 space-y-6">
                  <Tile
                    icon={blocked ? OctagonAlert : Octagon}
                    title={blocked ? 'AI OS Access: Stopped' : 'AI OS Access: Active'}
                    subtitle={blocked ? 'AI writes are blocked' : 'AI tools can write where enabled'}
                    actionText={blocked ? 'Resume' : 'Stop'}
                    onAction={() => (blocked ? resumeAccess() : confirmStop())}
                    variant={blocked ? 'default' : 'danger'}
                  />

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    <Card>
                      <CardContent className="space-y-3 p-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-md bg-muted p-2">
                            <Activity className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="font-medium">Emulation Analytics</div>
                            <div className="text-xs text-muted-foreground">
                              Share emulator usage telemetry for AI improvement.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Enable data sharing</Label>
                          <Switch
                            checked={Boolean(globalAnalytics.enabled)}
                            onCheckedChange={(checked) => updateGlobalAnalytics('enabled', checked)}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Apps still require explicit opt-in. This only controls the global analytics switch.
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="space-y-3 p-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-md bg-muted p-2">
                            <Shield className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="font-medium">Trust Mode (EHCO)</div>
                            <div className="text-xs text-muted-foreground">
                              Quick access to your MapleOS trust posture.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Enable Trust Mode</Label>
                          <Switch checked={trustEnabled} onCheckedChange={handleTrustModeChange} />
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Trust floor</span>
                          <span>{trustFloor.toFixed(2)}</span>
                        </div>
                        <Button variant="secondary" className="w-full" onClick={() => setActiveTab('trust')}>
                          Open Trust &amp; Governance
                        </Button>
                      </CardContent>
                    </Card>




                    <Card>
                      <CardContent className="space-y-3 p-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-md bg-muted p-2">
                            <Settings className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="font-medium">Default AI Model</div>
                            <div className="text-xs text-muted-foreground">
                              Choose MapleOS' preferred model for built-in apps.
                            </div>
                          </div>
                        </div>
                        <OSModelSelector />
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="space-y-3 p-4">
                        <div className="flex items-start gap-3">
                          <div className="rounded-md bg-muted p-2">
                            <Settings className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="font-medium">Desktop AI Taskbar</div>
                            <div className="text-xs text-muted-foreground">
                              Hide the desktop AI Taskbar for your user account.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Hide AI Taskbar</Label>
                          <Switch
                            checked={Boolean(osAISettings.hideDesktopTaskbar)}
                            onCheckedChange={handleHideTaskbarChange}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          When hidden, the top AI Taskbar is removed from the desktop until you turn it back on here.
                        </p>
                      </CardContent>
                    </Card>

                    <Tile
                      icon={Key}
                      title="Encryption"
                      subtitle="Encrypt AI output and manage keys"
                      actionText="Open"
                      onAction={openEncryptionSettings}
                    />
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Network className="h-5 w-5" /> Agent Access
                      </CardTitle>
                      <CardDescription>
                        Renderer guard for agent-marked file operations. Normal user actions are unaffected.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <div className="font-medium">Renderer guard</div>
                          <div className="text-xs text-muted-foreground">Status: {guardOn ? 'ON' : 'OFF'}</div>
                        </div>
                        <Switch
                          checked={guardOn}
                          onCheckedChange={(checked) => {
                            if (checked) enableAgentGuard();
                            else disableAgentGuard();
                          }}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Allowlist: {allowlist.length === 0 ? 'none' : `${allowlist.length} prefix(es)`}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => onOpenWindow?.('canxp-module', 'IntelligenceXP', '/intelligencexp')}>
                          Manage in IntelligenceXP
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => clearAllowlistPrefixes()}>
                          Clear allowlist
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="trust" className="mt-0 space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Trust &amp; Governance</CardTitle>
                      <CardDescription>
                        EHCO Trust evaluates intent, authorship, and presence before MapleOS executes AI work.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <p className="text-sm text-muted-foreground">
                        Use these controls to enforce grounded requests, citations for knowledge-based answers, and attestation before sealing.
                      </p>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                          <div>
                            <div className="font-medium">Enable Trust Mode by default</div>
                            <div className="text-sm text-muted-foreground">
                              Turn on EHCO Trust for MapleOS AI workflows automatically.
                            </div>
                          </div>
                          <Switch checked={trustEnabled} onCheckedChange={handleTrustModeChange} />
                        </div>

                        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                          <div>
                            <div className="font-medium">Require citations</div>
                            <div className="text-sm text-muted-foreground">
                              Require grounded citations for knowledge-based answers when trust is enabled.
                            </div>
                          </div>
                          <Switch checked={requireCitations} onCheckedChange={handleRequireCitationsChange} />
                        </div>

                        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                          <div>
                            <div className="font-medium">Require attestation</div>
                            <div className="text-sm text-muted-foreground">
                              Require attestation before new content is sealed into memory.
                            </div>
                          </div>
                          <Switch checked={requireAttestation} onCheckedChange={handleRequireAttestationChange} />
                        </div>
                      </div>

                      <div className="rounded-lg border p-4">
                        <div className="mb-3 flex items-center justify-between gap-4">
                          <div>
                            <div className="font-medium">Trust floor</div>
                            <div className="text-sm text-muted-foreground">
                              Higher values demand clearer intent, specificity, and grounded prompts.
                            </div>
                          </div>
                          <div className="text-sm font-medium">{trustFloor.toFixed(2)}</div>
                        </div>
                        <Slider value={[trustFloor]} min={0.6} max={0.95} step={0.01} onValueChange={handleTrustFloorChange} />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="privacy" className="mt-0 space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Privacy</CardTitle>
                      <CardDescription>
                        Control whether MapleOS saves your history and whether analytics are allowed.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                        <div>
                          <div className="font-medium">Save Chat History</div>
                          <div className="text-sm text-muted-foreground">
                            Turning this off removes saved chats from the server after confirmation.
                          </div>
                        </div>
                        <Switch checked={saveHistory} onCheckedChange={handleSaveHistoryChange} />
                      </div>

                      <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                        <div>
                          <div className="font-medium">Allow Usage Analytics</div>
                          <div className="text-sm text-muted-foreground">
                            MapleOS only loads analytics and attribution scripts when this is enabled.
                          </div>
                        </div>
                        <Switch checked={allowAnalytics} onCheckedChange={setAllowAnalytics} />
                      </div>

                      <p className="text-sm text-muted-foreground">
                        Accessibility note: analytics are invisible in normal workflows and only affect measurement scripts, not your interface layout or content visibility.
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="integrations" className="mt-0 space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold">API Integrations</h2>
                    <p className="text-sm text-muted-foreground">
                      Configure provider access for OpenAI, Gemini, DeepSeek, Cohere, MaplePT, and Ollama.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <OpenAISettings />
                    <GeminiSettings />
                    <DeepSeekSettings />
                    <CohereSettings />
                    <MaplePTSettings />
                    <OllamaSettings />
                  </div>
                </TabsContent>

                <TabsContent value="sealed" className="mt-0">
                  <SealedMemory />
                </TabsContent>
              </div>
            </ScrollArea>
          </div>
        </Tabs>

        <div className={`flex items-center gap-2 border-t px-3 py-2 text-xs sm:text-sm ${blocked ? 'bg-destructive/5' : 'bg-muted/40'}`}>
          {blocked ? (
            <>
              <OctagonAlert className="h-4 w-4 text-destructive" />
              <span className="font-medium text-destructive">AI Disabled</span>
              <span className="text-muted-foreground">AI access is blocked</span>
            </>
          ) : (
            <>
              <Shield className="h-4 w-4 text-emerald-600" />
              <span className="font-medium text-emerald-600">AI Enabled</span>
              <span className="text-muted-foreground">AI can access OS files where enabled</span>
            </>
          )}
        </div>

        <AlertDialog open={showStopDialog} onOpenChange={setShowStopDialog}>
          <AlertDialogContent className="border-destructive/40">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <OctagonAlert className="h-5 w-5" />
                Stop all AI access?
              </AlertDialogTitle>
              <AlertDialogDescription>
                AI will be jailed inside MapleOS. It will not be able to read or write your files until you resume access.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={performStop}>
                Stop access
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={confirmDeleteHistoryOpen} onOpenChange={setConfirmDeleteHistoryOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete saved chat history?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes your saved chats from the server and disables history going forward.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDeleteAndDisableHistory}>Delete and disable</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default AICenter;